import { Component, OnInit, ViewChild } from '@angular/core';
import { MENU_OPTIONS } from './advance-indicator-form.constants';
import { StrategiesService, AlgoStrategy, SearchSymbolResults, SymbolSearchService } from '../../../../providers';
import { ADVANCE_INDICATOR_INPUT_TYPE } from './advance-indicator-form.enums';
import { omit, last, find, cloneDeep } from 'lodash';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { StrategyParamsFormComponent } from '../strategy-params-form/strategy-params-form.component';
import { UntypedFormControl, Validators } from '@angular/forms';
import { NgxToastrService } from '../../../../providers';
import { AND_OR } from '../../strategy.enums';
import { AdvanceUserInputs, IndicatorLogicI, IndicatorRowI } from '@app/_shared/providers/strategies/strategies.interface';
import { Utils, DEFAULT_PRECISION } from '@app/_helpers';
import { AdvanceSymbolSearchComponent } from './components/advance-symbol-search/advance-symbol-search.component';
import { MatLegacyMenuTrigger as MatMenuTrigger } from '@angular/material/legacy-menu';


@Component({
  selector: 'app-advance-indicator-form',
  templateUrl: './advance-indicator-form.component.html',
  styleUrls: ['./advance-indicator-form.component.scss']
})
export class AdvanceIndicatorFormComponent implements OnInit {
  showAddInput = false;
  ALL_OPTIONS = cloneDeep(MENU_OPTIONS);
  menuOptions = {};
  andOrControls: Array<UntypedFormControl> = []
  inputs: Array<AdvanceUserInputs[]> = [[]];
  userInput = new UntypedFormControl();
  AND_OR = AND_OR;
  ADVANCE_INDICATOR_INPUT_TYPE = ADVANCE_INDICATOR_INPUT_TYPE;
  isLastConditionError: boolean;
  TIMEOUT = 3000;
  isRemoveDisabled = false;
  indicatorDescription: string[] = [];

  constructor(
    private strategyService: StrategiesService,
    private dialog: MatDialog,
    private matDialog: MatDialog,
    private symbolSearchService: SymbolSearchService
  ) { }

  @ViewChild(MatMenuTrigger) matMenuTrigger: MatMenuTrigger;

  ngOnInit() {
    this.getIndicators();
  }

  openIndicatorMenu() {
    this.matMenuTrigger.openMenu();
  }

  addNewIndicatorRow() {
    this.isLastConditionError = false;
    if (!last(this.inputs).length) {
      this.isLastConditionError = true;
      // removing this error after timeout
      setTimeout(() => {
        this.isLastConditionError = false;
      }, this.TIMEOUT)
      return;
    }
    this.inputs.push([]);
    this.showAddInput = true;
    this.updateOption();
    this.andOrControls.push(new UntypedFormControl(AND_OR.AND, [Validators.required]));
  }

  getIndicators() {
    this.strategyService.getIndicatorsList().subscribe(res => {
      this.indicatorDescription = res.map(indicator => indicator.description);
      this.ALL_OPTIONS.Indicators = res.map(indicator => indicator.name);
      this.updateOption();
    })
  }

  /**
   * Update options to display in condition builder menu.
   *
   * @memberof AdvanceIndicatorFormComponent
   */
  updateOption() {
    let excludedOptions = new Set<ADVANCE_INDICATOR_INPUT_TYPE>(); // Options to be removed from whole list.

    // If no element added in row (i.e. The is a new row) Only show indicator.
    if (!last(this.inputs).length) {
      this.isRemoveDisabled = true;
      excludedOptions.add(ADVANCE_INDICATOR_INPUT_TYPE.COMPARATOR);
      excludedOptions.add(ADVANCE_INDICATOR_INPUT_TYPE.MATH_OPERATION);
      excludedOptions.add(ADVANCE_INDICATOR_INPUT_TYPE.SYMBOL);
    } else {
      // Check the last indicator type
      const lastEntry = last(last(this.inputs));
      switch (lastEntry.type) {
        case ADVANCE_INDICATOR_INPUT_TYPE.NUMBER:
        case ADVANCE_INDICATOR_INPUT_TYPE.INDICATOR:
        case ADVANCE_INDICATOR_INPUT_TYPE.SYMBOL:
          // Indicator not to shown in case last entry was number or indicator.
          // After "Number" type entry, only show Math Operations or comparator.
          excludedOptions.add(ADVANCE_INDICATOR_INPUT_TYPE.INDICATOR);
          if (lastEntry.type !== ADVANCE_INDICATOR_INPUT_TYPE.INDICATOR) {
            excludedOptions.add(ADVANCE_INDICATOR_INPUT_TYPE.SYMBOL);
          }
          break;

        case ADVANCE_INDICATOR_INPUT_TYPE.MATH_OPERATION:
        case ADVANCE_INDICATOR_INPUT_TYPE.COMPARATOR:
          // Last element was of Math Operator or comparator then only indicator is allowed.
          excludedOptions.add(ADVANCE_INDICATOR_INPUT_TYPE.COMPARATOR);
          excludedOptions.add(ADVANCE_INDICATOR_INPUT_TYPE.MATH_OPERATION);
          excludedOptions.add(ADVANCE_INDICATOR_INPUT_TYPE.SYMBOL);
          break;
        default:
          break;
      }
    }

    // Only one comparator per row.
    if (find(last(this.inputs), { type: ADVANCE_INDICATOR_INPUT_TYPE.COMPARATOR })) {
      excludedOptions.add(ADVANCE_INDICATOR_INPUT_TYPE.COMPARATOR);
    }

    this.menuOptions = omit(this.ALL_OPTIONS, Array.from(excludedOptions));
  }

  checkFilterEvent(event: any) {
    const value: string = event.target.value;
    const key = event.key;

    if (value && !isNaN(key)) {
      const decimalPlaces = value.split('.')[1] || '';

      // Allow special key presses
      if (event.ctrlKey || event.metaKey ||
        key === 'ArrowLeft' || key === 'ArrowRight' || key === 'Backspace' ||
        key === 'Delete' || key === 'Tab' || key === 'Left' || key === 'Right') {
        return;
      }

      if (decimalPlaces.length >= DEFAULT_PRECISION) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
    }
  }

  openAdvanceSymbolSearch(panelKey: ADVANCE_INDICATOR_INPUT_TYPE, event: MouseEvent) {
    if (panelKey !== ADVANCE_INDICATOR_INPUT_TYPE.SYMBOL) {
      this.stopPropagation(event);
      return;
    }

    const dialog = this.matDialog.open(AdvanceSymbolSearchComponent, {
      height: '500px',
      width: '520px'
    });

    dialog.afterClosed().subscribe((symbol: SearchSymbolResults) => {
      if (symbol) {
        this.addInput(ADVANCE_INDICATOR_INPUT_TYPE.SYMBOL, `Of ${symbol.scrip_name}`, symbol);
      }
    })
  }

  removeRow() {
    this.inputs.pop();
    if (!this.inputs.length) {
      this.inputs.push([]);
      this.isRemoveDisabled = true;
    }
    this.showAddInput = true;
    this.andOrControls.pop();
    this.updateOption();
  }

  removeLast(idx: number) {
    if (!this.inputs[0].length) {
      return;
    }

    if (this.showAddInput) {
      this.showAddInput = false
      if (idx > 0 && !this.inputs[idx].length) {
        this.inputs.pop();
        this.andOrControls.pop();
      }
    }
    else {
      if (last(this.inputs).length) {
        this.inputs[idx].pop()
      } else {
        this.inputs.pop();
        this.andOrControls.pop();
        this.inputs[idx - 1].pop()
      }
    }
    if (!last(this.inputs).length) {
      this.showAddInput = true;
    }
    this.updateOption();
  }

  enterPress() {
    if (this.userInput.value && !isNaN(this.userInput.value)) {
      this.addInput(ADVANCE_INDICATOR_INPUT_TYPE.NUMBER, this.userInput.value.toString());
    }
  }

  addInput(type: ADVANCE_INDICATOR_INPUT_TYPE, input: string, symbol?: SearchSymbolResults) {
    this.userInput.reset();
    this.isRemoveDisabled = false;
    const indicatorObject = find(this.strategyService.params, (params) => params.name === input);
    if (type === ADVANCE_INDICATOR_INPUT_TYPE.INDICATOR && indicatorObject.inputParamterObjects.length) {
      const dialog = this.dialog.open(StrategyParamsFormComponent, {
        data: input
      });
      dialog.afterClosed().subscribe(data => {
        const valueStr = `${input}(${Object.values(data).join()})`
        last(this.inputs).push({
          type,
          value: valueStr,
          symbol
        });
        this.showAddInput = false;
        this.updateOption();
      });
    } else {
      last(this.inputs).push({
        type,
        value: input,
        symbol
      });
      this.showAddInput = false;
      this.updateOption();
    }
  }

  stopPropagation(event: MouseEvent) {
    event.stopPropagation();
  }

  updateInputValues(inp: AdvanceUserInputs) {
    const indicator = Utils.parseIndicatorValueString(inp.value);

    const indicatorObject = find(this.strategyService.params, (params) => params.name === indicator.name);

    if (inp.type === ADVANCE_INDICATOR_INPUT_TYPE.INDICATOR && indicatorObject.inputParamterObjects.length) {
      const dialog = this.dialog.open(StrategyParamsFormComponent, {
        data: indicator.name
      });
      dialog.afterClosed().subscribe(data => {
        const params = Object.values(data);
        const valueStr = Utils.formatIndicatorValueString(indicator.name, params.length ? params : null)
        inp.value = valueStr;
        this.showAddInput = false;
        this.updateOption();
      })
    }
  }

  fillUpdateForm(logic: IndicatorLogicI) {
    // Remove old (default) indicators before filling update indicators. 
    this.inputs = [[]];
    logic.indicatorRow.forEach((row, indicatorRowIndex) => {
      row.indicators.forEach((indicator) => {
        this.inputs[indicatorRowIndex].push({
          type: indicator.type,
          value: indicator.type === ADVANCE_INDICATOR_INPUT_TYPE.INDICATOR ?
            Utils.formatIndicatorValueString(indicator.name, indicator.params.length ? indicator.params : null) :
            indicator.name
        });

        if (indicator.type === ADVANCE_INDICATOR_INPUT_TYPE.INDICATOR && indicator.symbol) {
          const symbol = this.symbolSearchService.findScripsBySecurity([indicator.symbol])[0];
          if (symbol) {
            this.inputs[indicatorRowIndex].push({
              type: ADVANCE_INDICATOR_INPUT_TYPE.SYMBOL,
              value: `Of ${symbol.scrip_name}`,
              symbol: symbol
            })
          } else {
            console.error('Could not found underlying symbol in indicator. Updating will remove underlying symbol', indicator);
          }
        }
      })
      if (indicatorRowIndex < (logic.indicatorRow.length - 1)) {
        this.addNewIndicatorRow();
        last(this.andOrControls).setValue(logic.rowComparator[indicatorRowIndex]);
      }
    })

    if (last(this.inputs).length) {
      this.isRemoveDisabled = false;
    }
  }

  getData(): IndicatorLogicI {
    if (!this.isValid) { return; }
    return {
      rowComparator: this.andOrControls.map(form => form.value),
      indicatorRow: this.getIndicatorArray()
    }
  }

  getIndicatorArray() {
    const returnArray: IndicatorRowI[] = [];
    this.inputs.forEach((rowForm, idx) => {
      returnArray.push({
        indicators: []
      })
      rowForm.forEach(row => {
        if (row.type === ADVANCE_INDICATOR_INPUT_TYPE.INDICATOR) {
          const indicator = Utils.parseIndicatorValueString(row.value);
          returnArray[idx].indicators.push({
            name: indicator.name,
            params: indicator.params,
            type: row.type
          });
        } else if (row.type === ADVANCE_INDICATOR_INPUT_TYPE.SYMBOL) {
          last(returnArray[idx].indicators).symbol = {
            market_id: row.symbol.market_id,
            security_id: row.symbol.security_id
          }
        } else {
          returnArray[idx].indicators.push({
            name: row.value,
            params: [],
            type: row.type
          })
        }
      })
    });
    return returnArray;
  }

  get isValid() {
    let comparatorsIsValid = true;
    this.andOrControls.forEach(com => {
      comparatorsIsValid = comparatorsIsValid && com.valid;
    })
    return this.inputs.filter(i => i.length).length && comparatorsIsValid;
  }
}
